'use client';

import React, { useEffect, useState } from 'react';
import type { ConsentState } from './types';
import { readConsentCookie } from './consent-cookie';
import { useConsent, CONSENT_VERSION, POLICY_VERSION } from './useConsent';

interface CookieBannerProps {
  onManagePreferences?: () => void;
  privacyPolicyUrl?: string;
}

function buildAllConsent(accepted: boolean): ConsentState {
  // W2.2 fix (S9.A.01 Finding 12): stamp policy_version on the cookie payload
  // so a policy-change re-prompt can compare stored vs current.
  return {
    version: CONSENT_VERSION,
    policy_version: POLICY_VERSION,
    timestamp: new Date().toISOString(),
    categories: {
      necessary: true,
      functional: accepted,
      analytics: accepted,
      marketing: accepted,
    },
    source: 'user',
  };
}

export function CookieBanner({ onManagePreferences, privacyPolicyUrl = '/privacy' }: CookieBannerProps): React.JSX.Element | null {
  if (typeof window === 'undefined') return null;

  return <CookieBannerClient onManagePreferences={onManagePreferences} privacyPolicyUrl={privacyPolicyUrl} />;
}

function CookieBannerClient({ onManagePreferences, privacyPolicyUrl = '/privacy' }: CookieBannerProps): React.JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const { setConsent } = useConsent();

  useEffect(() => {
    const stored = readConsentCookie();
    // W2.2 fix (S9.A.01 Finding 12): re-prompt when cookie schema OR policy
    // version is stale. Also, when consent was set automatically by the GPC
    // path or a withdrawal sentinel, don't re-show the banner — those sources
    // represent a final state per CCPA + GDPR Art. 7(3) right-to-withdraw.
    const stale =
      !stored ||
      stored.version !== CONSENT_VERSION ||
      stored.policy_version !== POLICY_VERSION;
    const finalState = stored?.source === 'gpc' || stored?.source === 'withdrawn';
    if (stale && !finalState) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const handleAcceptAll = async (): Promise<void> => {
    await setConsent(buildAllConsent(true));
    setVisible(false);
  };

  const handleRejectNonEssential = async (): Promise<void> => {
    await setConsent(buildAllConsent(false));
    setVisible(false);
  };

  const handleManage = (): void => {
    onManagePreferences?.();
  };

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background p-4 shadow-lg"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground">
          We use cookies to improve your experience. You can choose which categories to allow.
          Necessary cookies are always active.{' '}
          <a href={privacyPolicyUrl} className="underline hover:no-underline">
            Privacy Policy
          </a>
        </p>
        <div className="flex flex-shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={handleRejectNonEssential}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Reject non-essential
          </button>
          <button
            type="button"
            onClick={handleManage}
            className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Manage preferences
          </button>
          <button
            type="button"
            onClick={handleAcceptAll}
            className="rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
